import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Users, MapPin, Calendar, ArrowRight, Newspaper, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

export default function NewsEventsPage() {
  return (
    <div data-testid="news-events-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E08B7A] via-[#E99A8A] to-[#F0A99A]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/20 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/30 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
              Latest News & Events
            </h1>
            <p className="text-xl text-white/90">
              Stay informed about OPAD's programmes and activities worldwide
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#E08B7A]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#7BC4E2]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-slate-700 leading-relaxed text-lg mb-4">
              Stay informed about the latest developments, programme updates, and upcoming activities from the <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong> across our global network of regional offices and community-based field units.
            </p>
            <p className="text-slate-700 leading-relaxed text-lg">
              Through strategic partnerships and locally driven initiatives, OPAD continues to implement impactful programmes that promote sustainable development, social inclusion, youth empowerment, and climate resilience across <span className="text-[#7BC4E2] font-semibold">Europe, Africa, Asia, and the Americas</span>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#7BC4E2]/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-tr-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E08B7A] rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">Upcoming Events</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-[#7BC4E2]/20 rounded-2xl flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-[#7BC4E2] font-bold text-2xl">27-30</span>
                  <span className="text-[#7BC4E2] text-sm font-medium">APR 2026</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Digital Skills Bootcamp for Marginalized Adults – Sweden</h3>
                  <p className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Stockholm & Botkyrka Municipality
                  </p>
                  <p className="text-slate-600">
                    Organized in collaboration with the <strong>Youth Capacity Building Organization (YIBO) – Sweden</strong>, this training programme aims to enhance digital competencies among marginalized adults, improve employability, and support inclusive participation in the digital economy.
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-[#E08B7A]/20 rounded-2xl flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-[#E08B7A] font-bold text-2xl">18-21</span>
                  <span className="text-[#E08B7A] text-sm font-medium">MAY 2026</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Empowering Digital Inclusion for Marginalized Young Adult Learners – Lithuania</h3>
                  <p className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Vilnius
                  </p>
                  <p className="text-slate-600">
                    Implemented by <strong>ASOCIACIJA AKTYVISTAI – Lithuania</strong>, this regional initiative focuses on strengthening digital access, e-learning participation, and socio-economic engagement among young adult learners across Europe.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programme Updates */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#4A7C59]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#E08B7A]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#4A7C59] rounded-xl flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">Programme Updates</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="h-2 bg-[#7BC4E2]"></div>
              <div className="p-6">
                <span className="inline-block bg-[#7BC4E2]/10 text-[#7BC4E2] text-xs font-medium px-3 py-1 rounded-full mb-3">Sweden</span>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Community Food Distribution Initiative</h3>
                <p className="text-slate-600 text-sm mb-4">
                  OPAD continues to provide food assistance and social support to underserved households through ongoing humanitarian outreach programmes in collaboration with local volunteers and community partners at Norsborg Botkyrka.
                </p>
                <p className="text-xs text-slate-400">Botkyrka, Sweden</p>
              </div>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="h-2 bg-[#E08B7A]"></div>
              <div className="p-6">
                <span className="inline-block bg-[#E08B7A]/10 text-[#E08B7A] text-xs font-medium px-3 py-1 rounded-full mb-3">Ethiopia</span>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Youth Skills Development Consortium</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Preparatory activities are underway for OPAD's multi-stakeholder consortium aimed at expanding youth access to vocational training, digital literacy, and employment pathways across underserved communities in Ethiopia.
                </p>
                <p className="text-xs text-slate-400">East Africa</p>
              </div>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="h-2 bg-[#4A7C59]"></div>
              <div className="p-6">
                <span className="inline-block bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-medium px-3 py-1 rounded-full mb-3">Remote</span>
                <h3 className="text-lg font-bold text-slate-900 mb-3">International Internship Programme</h3>
                <p className="text-slate-600 text-sm mb-4">
                  OPAD has successfully launched its 2026 Remote Internship Programme, engaging international students and graduates in SDG-focused research, project coordination, and advocacy initiatives.
                </p>
                <p className="text-xs text-slate-400">China Cohort</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stay Connected */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[350px] h-[300px] bg-white/10 rounded-bl-[150px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[120px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Globe className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-6">
              Stay Connected with OPAD
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Follow our latest programme developments, global events, and partnership opportunities as we continue to advance inclusive and sustainable development worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-white hover:bg-slate-100 text-[#4A7C59] rounded-full px-8 py-6 text-lg font-semibold">
                  Contact Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/get-involved">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#4A7C59] rounded-full px-8 py-6 text-lg font-semibold">
                  <Users className="w-5 h-5 mr-2" />
                  Get Involved
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
